import { useCallback, useEffect, useState } from 'react'
import { appsyncQuery } from '@api/appsync-client'

const GET_ORG_SSO_CONFIG = `
  query OrgSsoConfig {
    orgSsoConfig {
      status enforcement
      tenantId clientId clientSecretMasked metadataUrl cognitoProviderName
      updatedAt updatedBy
      domains { domain verified verificationToken verifiedAt }
    }
  }
`

const UPDATE_ORG_SSO_CONFIG = `
  mutation UpdateOrgSsoConfig($input: UpdateOrgSsoConfigInput!) {
    updateOrgSsoConfig(input: $input) {
      status enforcement tenantId clientId clientSecretMasked metadataUrl
      cognitoProviderName updatedAt updatedBy
      domains { domain verified verificationToken verifiedAt }
    }
  }
`

const SET_ENFORCEMENT = `
  mutation SetOrgSsoEnforcement($enforcement: String!) {
    setOrgSsoEnforcement(enforcement: $enforcement) {
      status enforcement
      domains { domain verified verificationToken verifiedAt }
    }
  }
`

const ADD_DOMAIN = `
  mutation AddOrgSsoDomain($domain: String!) {
    addOrgSsoDomain(domain: $domain) {
      domain verified verificationToken verifiedAt
    }
  }
`

const VERIFY_DOMAIN = `
  mutation VerifyOrgSsoDomain($domain: String!) {
    verifyOrgSsoDomain(domain: $domain) {
      domain verified verificationToken verifiedAt
    }
  }
`

const REMOVE_DOMAIN = `
  mutation RemoveOrgSsoDomain($domain: String!) {
    removeOrgSsoDomain(domain: $domain)
  }
`

const DISABLE_SSO = `
  mutation DisableOrgSso {
    disableOrgSso
  }
`

export function useSsoSettings() {
  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await appsyncQuery(GET_ORG_SSO_CONFIG)
      setConfig(data.orgSsoConfig)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const updateConfig = useCallback(async (input) => {
    const data = await appsyncQuery(UPDATE_ORG_SSO_CONFIG, { input })
    setConfig(data.updateOrgSsoConfig)
  }, [])

  const setEnforcement = useCallback(async (enforcement) => {
    await appsyncQuery(SET_ENFORCEMENT, { enforcement })
    load()
  }, [load])

  const addDomain = useCallback(async (domain) => {
    await appsyncQuery(ADD_DOMAIN, { domain })
    load()
  }, [load])

  const verifyDomain = useCallback(async (domain) => {
    await appsyncQuery(VERIFY_DOMAIN, { domain })
    load()
  }, [load])

  const removeDomain = useCallback(async (domain) => {
    await appsyncQuery(REMOVE_DOMAIN, { domain })
    load()
  }, [load])

  const disableSso = useCallback(async () => {
    await appsyncQuery(DISABLE_SSO)
    load()
  }, [load])

  return {
    config,
    loading,
    error,
    updateConfig,
    setEnforcement,
    addDomain,
    verifyDomain,
    removeDomain,
    disableSso,
    reload: load,
  }
}
